import { ActivityEventProcessType } from '@warebee/shared/import-converter';

export const inventoryKpis /*: WarehouseKPI[] */ = [
  // Inventory Accuracy
  {
    id: 'inventory_accuracy',
    category: 'inventory',
    title: 'Inventory Accuracy...',
    description: 'Percentage match between system inventory and physical count',
    unit: '%',
    formula: '(1 - ABS(book_qty - counted_qty) / book_qty) * 100',
    tags: ['iso-22400:QAS', 'cycle_count', 'storage'],
    benchmark: {
      standards: {
        site: {
          target: 99.5,
          warning: { lower: 98.5 },
          critical: { lower: 97 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P90D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 97 },
            { label: 'Warning', min: 97.01, max: 98.5 },
            { label: 'Target', min: 98.51, max: 99.5 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P90D',
    drillDimensions: ['location_class', 'sku_family', 'value_class'],
    alertTopic: 'warehouse.alerts.inventory.accuracy',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.INVENTORY_COUNT,
    ],
  },
  // Days of Supply
  {
    id: 'days_of_supply',
    category: 'inventory',
    title: 'Days of Supply',
    description:
      'Average number of days current inventory will last based on demand',
    unit: 'days',
    formula: '(current_inventory_units / average_daily_demand_units)',
    tags: ['storage', 'forecasting'],
    benchmark: {
      standards: {
        site: {
          target: 30,
          warning: { lower: 20 },
          critical: { lower: 10 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P90D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 10 },
            { label: 'Warning', min: 10.01, max: 20 },
            { label: 'Target', min: 20.01, max: 30 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P90D',
    forecastHorizon: 'P30D',
    drillDimensions: ['sku', 'product_category', 'supplier'],
    alertTopic: 'warehouse.alerts.inventory.days_supply',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.MANAGEMENT,
    ],
  },
  // Inventory Turnover
  {
    id: 'inventory_turnover',
    category: 'inventory',
    title: 'Inventory Turnover',
    description: 'Rate at which inventory is sold and replaced over a period',
    unit: 'turns/yr',
    formula: 'cost_of_goods_sold / average_inventory_value',
    tags: ['storage', 'efficiency'],
    benchmark: {
      standards: {
        site: {
          target: 12,
          warning: { lower: 8 },
          critical: { lower: 6 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P365D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 6 },
            { label: 'Warning', min: 6.01, max: 8 },
            { label: 'Target', min: 8.01, max: 12 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P365D',
    drillDimensions: ['product_category', 'value_class', 'storage_zone'],
    alertTopic: 'warehouse.alerts.inventory.turnover',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.SHIPPING,
      ActivityEventProcessType.RECEIVING,
    ],
  },
  // Obsolete Inventory
  {
    id: 'obsolete_inventory_percentage',
    category: 'inventory',
    title: 'Obsolete Inventory',
    description: 'Percentage of inventory with no demand in the past 90 days',
    unit: '%',
    formula:
      '(SUM(value_of_items_with_no_demand_90days) / SUM(total_inventory_value)) * 100',
    tags: ['storage', 'cost'],
    benchmark: {
      standards: {
        site: {
          target: 2,
          warning: { upper: 5 },
          critical: { upper: 10 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P90D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 10 },
            { label: 'Warning', min: 10.01, max: 5 },
            { label: 'Target', min: 5.01, max: 2 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P90D',
    drillDimensions: ['product_category', 'supplier', 'age_bucket'],
    alertTopic: 'warehouse.alerts.inventory.obsolete',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.MANAGEMENT_DATA,
    ],
  },
  // Storage Utilization
  {
    id: 'storage_utilization',
    category: 'inventory',
    title: 'Storage Utilization',
    description: 'Percentage of available storage locations that are occupied',
    unit: '%',
    formula: '(COUNT(occupied_locations) / COUNT(total_locations)) * 100',
    tags: ['storage', 'utilization'],
    benchmark: {
      standards: {
        site: {
          target: 85,
          warning: { lower: 70 },
          critical: { lower: 60 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 60 },
            { label: 'Warning', min: 60.01, max: 70 },
            { label: 'Target', min: 70.01, max: 85 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['storage_zone', 'location_type', 'aisle'],
    alertTopic: 'warehouse.alerts.inventory.storage_utilization',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.SLOTTING,
    ],
  },
  // Inventory Days on Hand
  {
    id: 'inventory_days_on_hand',
    category: 'inventory',
    title: 'Inventory Days on Hand',
    description:
      'Average number of days of inventory available based on current demand patterns',
    unit: 'days',
    formula: 'current_on_hand_quantity / average_daily_demand',
    tags: ['inventory', 'forecasting'],
    benchmark: {
      standards: {
        site: {
          target: 30,
          warning: { lower: 15 },
          critical: { lower: 7 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P90D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 7 },
            { label: 'Warning', min: 7.01, max: 15 },
            { label: 'Target', min: 15.01, max: 30 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P90D',
    forecastHorizon: 'P30D',
    drillDimensions: ['sku', 'sku_group', 'supplier'],
    alertTopic: 'warehouse.alerts.inventory.days_on_hand',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.MANAGEMENT,
    ],
  },
  // New Inventory Classification KPIs
  {
    id: 'inv_class_unit_price',
    category: 'inventory',
    title: 'Inventory Classification by Unit Price',
    description: 'ABC classification of inventory based on unit price',
    unit: 'items',
    formula: 'COUNT(items) GROUP BY price_category',
    tags: ['inventory', 'classification', 'price'],
    benchmark: {
      standards: {
        site: {
          target: 80,
          warning: { lower: 70 },
          critical: { lower: 60 },
          basis: 'ABC analysis standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P90D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 60 },
            { label: 'Warning', min: 60.01, max: 70 },
            { label: 'Target', min: 70.01, max: 80 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P90D',
    drillDimensions: ['price_category', 'product_category', 'supplier'],
    alertTopic: 'warehouse.alerts.inventory.classification_price',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.MANAGEMENT_DATA,
    ],
  },
  {
    id: 'inv_class_annual_consumption',
    category: 'inventory',
    title: 'Inventory Classification by Annual Consumption',
    description:
      'ABC classification of inventory based on annual consumption quantity',
    unit: 'items',
    formula: 'COUNT(items) GROUP BY consumption_category',
    tags: ['inventory', 'classification', 'consumption'],
    benchmark: {
      standards: {
        site: {
          target: 80,
          warning: { lower: 70 },
          critical: { lower: 60 },
          basis: 'ABC analysis standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P365D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 60 },
            { label: 'Warning', min: 60.01, max: 70 },
            { label: 'Target', min: 70.01, max: 80 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P365D',
    drillDimensions: ['consumption_category', 'product_category', 'supplier'],
    alertTopic: 'warehouse.alerts.inventory.classification_consumption',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.MANAGEMENT_DATA,
    ],
  },
  {
    id: 'inv_class_total_value',
    category: 'inventory',
    title: 'Inventory Classification by Total Value',
    description: 'ABC classification of inventory based on total stock value',
    unit: 'items',
    formula: 'COUNT(items) GROUP BY value_category',
    tags: ['inventory', 'classification', 'value'],
    benchmark: {
      standards: {
        site: {
          target: 80,
          warning: { lower: 70 },
          critical: { lower: 60 },
          basis: 'ABC analysis standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P90D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 60 },
            { label: 'Warning', min: 60.01, max: 70 },
            { label: 'Target', min: 70.01, max: 80 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P90D',
    drillDimensions: ['value_category', 'product_category', 'supplier'],
    alertTopic: 'warehouse.alerts.inventory.classification_value',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.MANAGEMENT_DATA,
    ],
  },
  {
    id: 'inv_class_usage_value',
    category: 'inventory',
    title: 'Inventory Classification by Usage and Value',
    description:
      'XYZ-ABC cross-classification of inventory based on usage predictability and value',
    unit: 'items',
    formula: 'COUNT(items) GROUP BY usage_value_category',
    tags: ['inventory', 'classification', 'usage', 'value'],
    benchmark: {
      standards: {
        site: {
          target: 80,
          warning: { lower: 70 },
          critical: { lower: 60 },
          basis: 'XYZ-ABC analysis standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P180D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 60 },
            { label: 'Warning', min: 60.01, max: 70 },
            { label: 'Target', min: 70.01, max: 80 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P180D',
    drillDimensions: ['usage_value_category', 'product_category', 'supplier'],
    alertTopic: 'warehouse.alerts.inventory.classification_usage_value',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.MANAGEMENT_DATA,
    ],
  },
  {
    id: 'inv_class_profit_contribution',
    category: 'inventory',
    title: 'Inventory Classification by Profit Contribution',
    description: 'Classification of inventory based on contribution to profits',
    unit: 'items',
    formula: 'COUNT(items) GROUP BY profit_category',
    tags: ['inventory', 'classification', 'profit'],
    benchmark: {
      standards: {
        site: {
          target: 80,
          warning: { lower: 70 },
          critical: { lower: 60 },
          basis: 'Profit analysis standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P365D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 60 },
            { label: 'Warning', min: 60.01, max: 70 },
            { label: 'Target', min: 70.01, max: 80 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P365D',
    drillDimensions: ['profit_category', 'product_category', 'supplier'],
    alertTopic: 'warehouse.alerts.inventory.classification_profit',
    processType: [
      ActivityEventProcessType.INVENTORY,
      ActivityEventProcessType.MANAGEMENT_DATA,
    ],
  },
  // Today/Operations KPIs
  {
    id: 'open_warehouse_orders_by_queue',
    category: 'today',
    title: 'Open Warehouse Orders by Queue',
    description: 'Number of open warehouse orders by processing queue',
    unit: 'orders',
    formula: 'COUNT(orders WHERE status = "open") GROUP BY queue',
    tags: ['operations', 'today', 'orders'],
    benchmark: {
      standards: {
        site: {
          target: 10,
          warning: { upper: 25 },
          critical: { upper: 50 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 10 },
            { label: 'Warning', min: 10.01, max: 25 },
            { label: 'Critical', min: 25.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['queue', 'priority', 'order_type'],
    alertTopic: 'warehouse.alerts.today.open_orders_queue',
    processType: [ActivityEventProcessType.MANAGEMENT],
  },
  {
    id: 'confirmed_warehouse_orders_by_queue',
    category: 'today',
    title: 'Confirmed Warehouse Orders by Queue',
    description: 'Number of confirmed warehouse orders by processing queue',
    unit: 'orders',
    formula: 'COUNT(orders WHERE status = "confirmed") GROUP BY queue',
    tags: ['operations', 'today', 'orders'],
    benchmark: {
      standards: {
        site: {
          target: 50,
          warning: { lower: 30 },
          critical: { lower: 15 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 15 },
            { label: 'Warning', min: 15.01, max: 30 },
            { label: 'Target', min: 30.01, max: 50 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['queue', 'priority', 'order_type'],
    alertTopic: 'warehouse.alerts.today.confirmed_orders_queue',
    processType: [ActivityEventProcessType.MANAGEMENT],
  },
  {
    id: 'open_pick_tasks_by_area',
    category: 'today',
    title: 'Open Pick Tasks by Activity Area',
    description: 'Number of open pick warehouse tasks by activity area',
    unit: 'tasks',
    formula:
      'COUNT(tasks WHERE type = "pick" AND status = "open") GROUP BY activity_area',
    tags: ['operations', 'today', 'tasks', 'picking'],
    benchmark: {
      standards: {
        site: {
          target: 15,
          warning: { upper: 30 },
          critical: { upper: 50 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 15 },
            { label: 'Warning', min: 15.01, max: 30 },
            { label: 'Critical', min: 30.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['activity_area', 'priority', 'user_group'],
    alertTopic: 'warehouse.alerts.today.open_pick_tasks_area',
    processType: [ActivityEventProcessType.PICKING],
  },
  {
    id: 'open_putaway_tasks_by_area',
    category: 'today',
    title: 'Open Putaway Tasks by Area',
    description: 'Number of open putaway warehouse tasks by activity area',
    unit: 'tasks',
    formula:
      'COUNT(tasks WHERE type = "putaway" AND status = "open") GROUP BY activity_area',
    tags: ['operations', 'today', 'tasks', 'putaway'],
    benchmark: {
      standards: {
        site: {
          target: 15,
          warning: { upper: 30 },
          critical: { upper: 50 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 15 },
            { label: 'Warning', min: 15.01, max: 30 },
            { label: 'Critical', min: 30.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['activity_area', 'priority', 'user_group'],
    alertTopic: 'warehouse.alerts.today.open_putaway_tasks_area',
    processType: [ActivityEventProcessType.PUTAWAY],
  },
  {
    id: 'open_tasks_by_area',
    category: 'today',
    title: 'Open Tasks by Activity Area',
    description: 'Number of open warehouse tasks by activity area',
    unit: 'tasks',
    formula: 'COUNT(tasks WHERE status = "open") GROUP BY workzone',
    tags: ['operations', 'today', 'tasks'],
    benchmark: {
      standards: {
        site: {
          target: 30,
          warning: { upper: 60 },
          critical: { upper: 100 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 30 },
            { label: 'Warning', min: 30.01, max: 60 },
            { label: 'Critical', min: 60.01, max: 200 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['activity_area', 'priority', 'task_type'],
    alertTopic: 'warehouse.alerts.today.open_tasks_area',
    processType: [ActivityEventProcessType.MANAGEMENT],
  },
  {
    id: 'open_tasks_by_overdue_time',
    category: 'today',
    title: 'Open Tasks by Overdue Time',
    description: 'Number of open warehouse tasks by overdue time in hours',
    unit: 'tasks',
    formula:
      'COUNT(tasks WHERE status = "open" AND current_time > due_time) GROUP BY FLOOR((current_time - due_time)/3600)',
    tags: ['operations', 'today', 'tasks', 'overdue'],
    benchmark: {
      standards: {
        site: {
          target: 0,
          warning: { upper: 5 },
          critical: { upper: 15 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 0 },
            { label: 'Warning', min: 0.01, max: 5 },
            { label: 'Critical', min: 5.01, max: 50 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['overdue_hours', 'task_type', 'priority'],
    alertTopic: 'warehouse.alerts.today.open_tasks_overdue',
    processType: [ActivityEventProcessType.MANAGEMENT],
  },
  {
    id: 'open_tasks_by_process_category',
    category: 'today',
    title: 'Open Tasks by Process Category',
    description: 'Number of open warehouse tasks by warehouse process category',
    unit: 'tasks',
    formula: 'COUNT(tasks WHERE status = "open") GROUP BY process_category',
    tags: ['operations', 'today', 'tasks', 'process'],
    benchmark: {
      standards: {
        site: {
          target: 20,
          warning: { upper: 40 },
          critical: { upper: 60 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 20 },
            { label: 'Warning', min: 20.01, max: 40 },
            { label: 'Critical', min: 40.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['process_category', 'priority', 'user_group'],
    alertTopic: 'warehouse.alerts.today.open_tasks_process_category',
    processType: [ActivityEventProcessType.MANAGEMENT],
  },
  {
    id: 'open_tasks_by_process_type',
    category: 'today',
    title: 'Open Tasks by Process Type',
    description: 'Number of open warehouse tasks by warehouse process type',
    unit: 'tasks',
    formula: 'COUNT(tasks WHERE status = "open") GROUP BY process_type',
    tags: ['operations', 'today', 'tasks', 'process'],
    benchmark: {
      standards: {
        site: {
          target: 15,
          warning: { upper: 30 },
          critical: { upper: 50 },
          basis: 'Operations standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P1D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 15 },
            { label: 'Warning', min: 15.01, max: 30 },
            { label: 'Critical', min: 30.01, max: 80 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P1D',
    drillDimensions: ['process_type', 'priority', 'user_group'],
    alertTopic: 'warehouse.alerts.today.open_tasks_process_type',
    processType: [ActivityEventProcessType.MANAGEMENT],
  },
];

export default inventoryKpis;
